import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useDataSource } from '../DataSource';
import Avatar from '@material-ui/core/Avatar';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import AddProductDrawer from '../AddProductDrawer';
import Checkbox from '../Checkbox';
import DataList from '../DataList';
import SelectionBar from '../SelectionBar';
import TextField from '../TextField';
import SkeletonBody from '@afosto/components-react/SkeletonBody';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import translations from './translations';
import styled from 'styled-components';
import clsx from 'clsx';

const StackItemQuantity = styled.div`
  width: 70px;
  flex-shrink: 0;
`;

const StackItem = styled(ListItem)`
  && {
    &.selection {
      padding-left: 0;
    }
  }
`;

const StackItemContainer = ({ title, children, allowCreate, onCreate, allowNegativeQuantity }) => {
  const intl = useIntl();
  const [showCreateItem, setShowCreateItem] = useState(false);

  /**
   * Handle create stack item.
   * @param {object} item
   */
  const handleCreateStackItem = async item => {
    if (onCreate) {
      await onCreate(item);
    }

    if (!item.addAnotherProduct) {
      setShowCreateItem(false);
    }
  };

  return (
    <>
      <Card style={{ marginBottom: 8 }}>
        <CardHeader
          title={title || intl.formatMessage(translations.title)}
          titleTypographyProps={{ variant: 'h6' }}
          action={
            allowCreate && (
              <Button color="primary" variant="outlined" onClick={() => setShowCreateItem(true)}>
                {intl.formatMessage(translations.button.create.label)}
              </Button>
            )
          }
        />
        {children}
      </Card>
      {allowCreate && (
        <AddProductDrawer
          formProps={{ allowNegativeQuantity }}
          isOpen={showCreateItem}
          onAdd={handleCreateStackItem}
          onClose={() => setShowCreateItem(false)}
          title={intl.formatMessage(translations.createItem.title)}
        />
      )}
    </>
  );
};

StackItemContainer.propTypes = {
  allowCreate: PropTypes.bool,
  allowNegativeQuantity: PropTypes.bool,
  children: PropTypes.node.isRequired,
  onCreate: PropTypes.func,
  title: PropTypes.string,
};

StackItemContainer.defaultProps = {
  allowCreate: false,
  allowNegativeQuantity: false,
  onCreate: undefined,
  title: undefined,
};

const StackItemList = ({
  items,
  title,
  ContainerElement,
  allowChangeQuantity,
  onChangeQuantity,
  allowCreate,
  onCreate,
  allowRemove,
  onRemove,
  allowNegativeQuantity,
  renderItem,
  renderSkeletonItem,
  isLoading,
}) => {
  const intl = useIntl();
  const modules = [...(allowRemove ? ['selection'] : [])];
  const dataSource = useDataSource(items, { modules });
  const { data = [], selection: selectionModule = {}, hasModule } = dataSource;
  const {
    allItemsSelected,
    setDeselectedItems,
    setSelectedItems,
    deselectItems,
    selectItems,
    deselectedItems = [],
    selectedItems = [],
  } = selectionModule;
  const dataSourceRef = useRef(dataSource);
  const [isIndeterminate, setIsIndeterminate] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const hasSelectionModule = hasModule('selection');
  const itemIds = data.map(item => item.id);

  /**
   * Re-calculates whether the row selection checkbox should be checked or be indeterminate.
   */
  useEffect(() => {
    if (hasSelectionModule) {
      const selectedRowsOnPage = selectedItems.filter(itemId => itemIds.includes(itemId));

      let indeterminate =
        selectedRowsOnPage.length > 0 && itemIds.length - selectedRowsOnPage.length > 0;
      let checked = selectedRowsOnPage.length > 0 && selectedRowsOnPage.length === itemIds.length;

      if (allItemsSelected) {
        const deselectedRowsOnPage = deselectedItems.filter(itemId => itemIds.includes(itemId));

        indeterminate =
          deselectedRowsOnPage.length > 0 && itemIds.length - deselectedRowsOnPage.length > 0;
        checked = deselectedRowsOnPage.length === 0;
      }

      setIsIndeterminate(indeterminate);
      setIsChecked(checked);
    }
  }, [hasSelectionModule, itemIds, selectedItems, deselectedItems, allItemsSelected]);

  useEffect(() => {
    dataSourceRef.current = dataSource;
  });

  useEffect(() => {
    dataSourceRef.current.setIsLoadingData(isLoading);
  }, [isLoading]);

  const handleChangeQuantity = item => async event => {
    if (onChangeQuantity) {
      await onChangeQuantity(item)(event);
    }
  };

  const getItemQuantity = itemId => {
    const item = findObjectInArray(items, 'id', itemId);
    return item?.quantity ?? 0;
  };

  const renderStackItem = (item, { SelectionComponent }) => {
    const itemQuantity = getItemQuantity(item.id);

    if (renderItem) {
      return renderItem({ ...item, quantity: itemQuantity }, { SelectionComponent });
    }

    return (
      <StackItem key={item.reference} className={clsx({ selection: hasSelectionModule })}>
        {SelectionComponent && <SelectionComponent />}
        {item.image && (
          <ListItemAvatar>
            <Avatar alt={item.description} src={item.image} />
          </ListItemAvatar>
        )}
        <ListItemText primary={item.description} secondary={item.sku} />
        <StackItemQuantity>
          {allowChangeQuantity ? (
            <TextField
              type="number"
              inline={false}
              isEditing
              name={`item[${item.id}]`}
              variant="outlined"
              margin="dense"
              fullWidth
              value={itemQuantity}
              inputProps={
                !allowNegativeQuantity
                  ? {
                      min: 1,
                    }
                  : {}
              }
              onChange={handleChangeQuantity(item)}
            />
          ) : (
            itemQuantity
          )}
        </StackItemQuantity>
      </StackItem>
    );
  };

  const renderStackSkeletonItem = key => (
    <ListItem key={key}>
      <ListItemText>
        <SkeletonBody lines={2} />
      </ListItemText>
    </ListItem>
  );

  const handleRemoveItems = async () => {
    if (onRemove) {
      await onRemove(selectedItems, dataSource);
    }

    setSelectedItems([]);
  };

  const handleSelect = selectAll => () => {
    if (selectAll) {
      selectItems(itemIds);
      setDeselectedItems(deselectedItems.filter(itemId => !itemIds.includes(itemId)));
    } else {
      setSelectedItems(selectedItems.filter(itemId => !itemIds.includes(itemId)));
      deselectItems(itemIds);
    }
  };

  const bulkActions = [
    {
      key: 'removeItems',
      render: () => (
        <Button onClick={handleRemoveItems} key="removeItems">
          <Typography variant="button">
            {intl.formatMessage(translations.actions.remove.label)}
          </Typography>
        </Button>
      ),
    },
  ];

  return (
    <ContainerElement
      title={title}
      allowCreate={allowCreate}
      onCreate={onCreate}
      allowNegativeQuantity={allowNegativeQuantity}
      dataSource={dataSource}
    >
      {selectedItems?.length > 0 && (
        <SelectionBar
          selectionCount={selectedItems.length}
          bulkActions={bulkActions}
          onClearSelection={() => setSelectedItems([])}
        />
      )}
      {hasSelectionModule && dataSource?.data?.length > 0 && (
        <>
          <Box px={2}>
            <Checkbox
              name="selectAll"
              checked={isChecked}
              indeterminate={isIndeterminate}
              onChange={handleSelect(!isChecked || isIndeterminate)}
              register={false}
            />
          </Box>
          <Divider />
        </>
      )}
      <DataList
        dataSource={dataSource}
        renderItem={renderStackItem}
        renderSkeletonItem={renderSkeletonItem ?? renderStackSkeletonItem}
        SelectionBarComponent={null}
        emptyMessage={intl.formatMessage(translations.emptyMessage)}
        emptyMessageAlignment="left"
      />
    </ContainerElement>
  );
};

StackItemList.propTypes = {
  items: PropTypes.array,
  title: PropTypes.string,
  ContainerElement: PropTypes.oneOfType([PropTypes.node, PropTypes.func, PropTypes.string]),
  allowChangeQuantity: PropTypes.bool,
  onChangeQuantity: PropTypes.func,
  allowCreate: PropTypes.bool,
  onCreate: PropTypes.func,
  allowRemove: PropTypes.bool,
  onRemove: PropTypes.func,
  allowNegativeQuantity: PropTypes.bool,
  renderItem: PropTypes.func,
  renderSkeletonItem: PropTypes.func,
  isLoading: PropTypes.bool,
};

StackItemList.defaultProps = {
  items: [],
  title: undefined,
  ContainerElement: StackItemContainer,
  allowChangeQuantity: false,
  onChangeQuantity: undefined,
  allowCreate: false,
  onCreate: undefined,
  allowRemove: false,
  onRemove: undefined,
  allowNegativeQuantity: false,
  renderItem: undefined,
  renderSkeletonItem: undefined,
  isLoading: false,
};

export default StackItemList;
